import { EMPTY_REMINDER_STATE, readReminderState, type ReminderState } from '@wiztime/core';
import { create } from 'zustand';
import { readStorage, removeStorage, writeStorage } from '../../lib/safeStorage';
import { logLocalSetting } from '../../lib/localSettingLog';
import { FLAG_ON, StorageKey } from '../../lib/storageKeys';

/** The per-device reminder switch (F7): stored in localStorage, never synced; off by default. */
interface ReminderPrefs {
  readonly enabled: boolean;
  setEnabled(enabled: boolean): void;
  /** Re-read the switch (another tab changed it). */
  reload(): void;
}

const storedEnabled = (): boolean => readStorage(StorageKey.ReminderEnabled) === FLAG_ON;

export const useReminderPrefs = create<ReminderPrefs>()((set) => ({
  enabled: storedEnabled(),
  setEnabled(enabled) {
    if (enabled) writeStorage(StorageKey.ReminderEnabled, FLAG_ON);
    else removeStorage(StorageKey.ReminderEnabled);
    set({ enabled });
    logLocalSetting(StorageKey.ReminderEnabled, enabled);
  },
  reload() {
    set({ enabled: storedEnabled() });
  },
}));

export function readStoredReminderState(): ReminderState {
  const raw = readStorage(StorageKey.ReminderState);
  if (raw === null) return EMPTY_REMINDER_STATE;
  try {
    return readReminderState(JSON.parse(raw));
  } catch {
    return EMPTY_REMINDER_STATE;
  }
}

export function writeStoredReminderState(state: ReminderState): void {
  writeStorage(StorageKey.ReminderState, JSON.stringify(state));
}
